import React, { useEffect, useRef, useState } from 'react';
import { Button, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

type AnnotationTool = 'draw' | 'label';

interface AnnotationCanvasProps {
  snapshotDataUrl: string;
  onSave: (annotatedDataUrl: string) => void;
}

/** Draw and label structures on a captured eyepiece image. */
const AnnotationCanvas: React.FC<AnnotationCanvasProps> = ({ snapshotDataUrl, onSave }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [tool, setTool] = useState<AnnotationTool>('draw');
  const [label, setLabel] = useState('');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    const image = new Image();
    image.onload = () => {
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      context.drawImage(image, 0, 0);
      setReady(true);
    };
    image.src = snapshotDataUrl;
  }, [snapshotDataUrl]);

  const eventPoint = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = event.currentTarget;
    const rect = canvas.getBoundingClientRect();
    return { x: (event.clientX - rect.left) * (canvas.width / rect.width), y: (event.clientY - rect.top) * (canvas.height / rect.height) };
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = event.currentTarget;
    const context = canvas.getContext('2d');
    if (!context) return;
    const point = eventPoint(event);
    if (tool === 'label') {
      if (label.trim()) {
        context.font = 'bold 20px sans-serif';
        context.lineWidth = 4; context.strokeStyle = 'rgba(14, 24, 36, .8)'; context.fillStyle = '#fff36b';
        context.strokeText(label.trim(), point.x, point.y); context.fillText(label.trim(), point.x, point.y);
      }
      return;
    }
    canvas.setPointerCapture(event.pointerId);
    drawing.current = true;
    context.beginPath(); context.moveTo(point.x, point.y); context.strokeStyle = '#ffef64'; context.lineWidth = Math.max(3, canvas.width / 180); context.lineCap = 'round';
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const context = event.currentTarget.getContext('2d');
    if (!context) return;
    const point = eventPoint(event); context.lineTo(point.x, point.y); context.stroke();
  };

  const stopDrawing = () => { drawing.current = false; };
  const save = () => {
    const canvas = canvasRef.current;
    if (canvas) onSave(canvas.toDataURL('image/jpeg', 0.9));
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2">Draw to mark a structure, or select Label and click where its name should appear.</Typography>
      <ToggleButtonGroup size="small" exclusive value={tool} onChange={(_, value: AnnotationTool | null) => value && setTool(value)} aria-label="Annotation tool">
        <ToggleButton value="draw">Draw</ToggleButton><ToggleButton value="label">Place label</ToggleButton>
      </ToggleButtonGroup>
      {tool === 'label' && <TextField size="small" label="Structure label" value={label} onChange={(event) => setLabel(event.target.value)} />}
      <canvas ref={canvasRef} className="annotation-canvas" aria-label="Snapshot annotation drawing area" onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={stopDrawing} onPointerCancel={stopDrawing} />
      <Button variant="contained" disabled={!ready} onClick={save}>Save annotated snapshot</Button>
    </Stack>
  );
};

export default AnnotationCanvas;
